/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */
import _ from 'lodash';
import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';

import PhoneFormatBehavior from '@/behaviors/PhoneFormatBehavior';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import showErrorMessage from '@/util/showErrorMessage';
import { validateForm } from '@/util/ParsleyUtils';

import UpdateAWPackageFDRInfo from '../../services/UpdateAWPackageFDRInfo';

import tmplAWPackageFDRInformationView from './AWPackageFDRInformationView.hbs';

const AWPackageFDRInformationView = View.extend({
  template: tmplAWPackageFDRInformationView,

  ui: {
    informationFDRForm: '.information-fdr-form',
    awPackageInfo: '.aw-package-info',
    contractStartDate: 'input[name="contractStartDate"]',
    contractEndDate: 'input[name="contractEndDate"]',
    phoneFormat: '.phone-format',
    koInformation: 'select[name="koInformation"]',
    koInformationForm: '#koInformationForm'
  },

  events: {
    'change @ui.koInformation': 'onChangePrepopulateKOInfo'
  },

  behaviors: {
    telephone: PhoneFormatBehavior
  },

  onAttach() {
    if (this.model.get('showValidation')) {
      this.validate();
    }
  },

  onRender() {
    const awPackage = this.model.get('awPackage');
    const fdrInfo = this.model.get('awPackageFDRInfo');

    if (!fdrInfo.fundingCategoryId) {
      fdrInfo.fundingCategoryId = 1; //Default to 1 (Operations) if not defined
    }

    this.ui.contractStartDate.datepicker({ format: 'mm/dd/yyyy' });
    this.ui.contractStartDate.datepicker('setDate', awPackage.contractStartDate);

    this.ui.contractEndDate.datepicker({ format: 'mm/dd/yyyy' });
    this.ui.contractEndDate.datepicker('setDate', awPackage.contractEndDate);

    Syphon.deserialize(this.ui.informationFDRForm, fdrInfo);
    Syphon.deserialize(this.ui.awPackageInfo, awPackage);

    if (!this.model.get('editable')) {
      this.toggleForm(false);
    }
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
  },

  onChangePrepopulateKOInfo(e) {
    e?.preventDefault();
    const selectedKoId = this.ui.koInformation.val();
    const koDefaultInformation = _.filter(this.model.get('koDefaults'), (ko) => {
      return ko.id === selectedKoId;
    });
    Syphon.deserialize(this.ui.koInformationForm, koDefaultInformation);
  },

  async validate() {
    try {
      await Promise.all([validateForm(this.ui.informationFDRForm)]);
      this.trigger('no:errors');
    } catch (err) {
      this.trigger('has:errors');
      return false;
    }
  },

  async onSave() {
    if (!this.model.get('editable')) {
      return;
    }

    const formData = Syphon.serialize(this.ui.informationFDRForm);
    formData.awPackageId = this.model.get('awPackage').id;

    try {
      await UpdateAWPackageFDRInfo(formData);
      showSuccessMessage({
        code: 'saveSuccess',
        title: 'Save Success',
        message: 'AW Package Information Saved.',
        hideAfter: 1
      });
    } catch (err) {
      showErrorMessage({
        errorCode: 'saveError',
        errorTitle: 'Save Error',
        errorMessage: 'Error Saving File.'
      });
      throw new Error(err);
    }
  }
});

export default AWPackageFDRInformationView;
